import { cloneElement, CSSProperties, FC, isValidElement, ReactNode, RefObject, useCallback, useMemo } from 'react';
import type { IconType } from 'react-icons';
import {
    TbAddressBook,
    TbAdjustments,
    TbArticle,
    TbBell,
    TbBriefcase,
    TbBrowserCheck,
    TbBuilding,
    TbCalendar,
    TbChartBar,
    TbClock,
    TbCloudUpload,
    TbCreditCard,
    TbDatabase,
    TbDeviceDesktop,
    TbDownload,
    TbEye,
    TbId,
    TbInfoCircle,
    TbLifebuoy,
    TbLockAccess,
    TbMail,
    TbLock,
    TbMessageCircle,
    TbNote,
    TbPalette,
    TbPhoto,
    TbPlugConnected,
    TbPuzzle,
    TbRefresh,
    TbSettings,
    TbShieldLock,
    TbTag,
    TbTrash,
    TbUser,
    TbUsers,
    TbUsersGroup,
    TbUserShield,
    TbWebhook,
    TbWorldCheck,
} from 'react-icons/tb';
import { useLocation } from 'react-router-dom';

import { MemoTableOfContents } from '@/shared';
import type { MemoTableOfContentsItem } from '@/shared';

import { SettingsSearchEntry, settingsSearchPages } from '../../../../settingsSearchRegistry';

interface SettingsTableOfContentsProps {
    contentRef: RefObject<HTMLElement | null>;
}

interface SettingsIconElementProps {
    size?: number | string;
    style?: CSSProperties;
}

interface SettingsIconRule {
    Icon: IconType;
    pattern: RegExp;
}

const SETTINGS_ICON_SIZE = '0.75rem';

const settingsIconRules: SettingsIconRule[] = [
    { pattern: /danger|delete|remove|force.*logout|revoke/, Icon: TbTrash },
    { pattern: /slack/, Icon: TbMessageCircle },
    { pattern: /e-?mail/, Icon: TbMail },
    { pattern: /digest|schedule|calendar/, Icon: TbCalendar },
    { pattern: /notification|alert|push/, Icon: TbBell },
    { pattern: /avatar|logo|image/, Icon: TbPhoto },
    { pattern: /account information|profile|bio/, Icon: TbId },
    { pattern: /first name|last name|username|workspace name/, Icon: TbUser },
    { pattern: /company|workspace information/, Icon: TbBuilding },
    { pattern: /job title|\btitle\b/, Icon: TbBriefcase },
    { pattern: /description|notes?/, Icon: TbNote },
    { pattern: /workspace integration|workflows?|automations?/, Icon: TbPlugConnected },
    { pattern: /data source|database|integration/, Icon: TbDatabase },
    { pattern: /theme|appearance|branding|whitelabel/, Icon: TbPalette },
    { pattern: /timezone|duration/, Icon: TbClock },
    { pattern: /reset/, Icon: TbRefresh },
    { pattern: /tags?|labels?/, Icon: TbTag },
    { pattern: /watchlists?/, Icon: TbEye },
    { pattern: /sessions?|devices?/, Icon: TbDeviceDesktop },
    { pattern: /authentication|passkeys?|two-factor|security|saml|identity/, Icon: TbShieldLock },
    { pattern: /download|export/, Icon: TbDownload },
    { pattern: /plans?|billing|invoice|credit|payment|card/, Icon: TbCreditCard },
    { pattern: /seats?|members?|active users?|guests?/, Icon: TbUsers },
    { pattern: /add-ons?|features?/, Icon: TbPuzzle },
    { pattern: /support/, Icon: TbLifebuoy },
    { pattern: /usage|overview|summary|breakdown|timeline/, Icon: TbChartBar },
    { pattern: /network|access/, Icon: TbLockAccess },
    { pattern: /domains?|locations?/, Icon: TbWorldCheck },
    { pattern: /directory|scim/, Icon: TbAddressBook },
    { pattern: /permissions?|roles?/, Icon: TbUserShield },
    { pattern: /teams?/, Icon: TbUsersGroup },
    { pattern: /api|keys?/, Icon: TbLock },
    { pattern: /webhooks?/, Icon: TbWebhook },
    { pattern: /oauth|applications?/, Icon: TbBrowserCheck },
    { pattern: /audit|logs?/, Icon: TbArticle },
    { pattern: /stream/, Icon: TbCloudUpload },
    { pattern: /about/, Icon: TbInfoCircle },
    { pattern: /general|preferences|methods?|options?/, Icon: TbAdjustments },
];

const normalize = (value: string) => value.trim().toLowerCase().replace(/\s+/g, ' ');

const flattenEntries = (entry: SettingsSearchEntry): SettingsSearchEntry[] => [
    entry,
    ...(entry.children ?? []).flatMap(flattenEntries),
];

const isRouteActive = (pathname: string, route: string) => pathname === route || pathname.startsWith(`${route}/`);

const normalizeRegisteredIcon = (icon?: ReactNode): ReactNode => {
    if (!isValidElement<SettingsIconElementProps>(icon)) {
        return icon;
    }

    return cloneElement(icon, {
        size: SETTINGS_ICON_SIZE,
        style: undefined,
    });
};

const resolveSemanticIcon = (title: string, key: string): ReactNode => {
    const rule =
        settingsIconRules.find(({ pattern }) => pattern.test(title)) ??
        settingsIconRules.find(({ pattern }) => pattern.test(key));
    const Icon = rule?.Icon ?? TbSettings;

    return <Icon size={SETTINGS_ICON_SIZE} />;
};

const SettingsTableOfContents: FC<SettingsTableOfContentsProps> = ({ contentRef }) => {
    const { pathname } = useLocation();
    const activePage = useMemo(
        () =>
            settingsSearchPages
                .filter((page) => isRouteActive(pathname, page.route))
                .sort((left, right) => right.route.length - left.route.length)[0],
        [pathname],
    );
    const activeEntries = useMemo(() => (activePage ? flattenEntries(activePage) : []), [activePage]);

    const getHeadingIcon = useCallback(
        (item: MemoTableOfContentsItem): ReactNode => {
            const elementKey = item.element.id.replace(/^settings-/, '');
            const normalizedTitle = normalize(item.title);
            const entry =
                activeEntries.find((candidate) => candidate.key === elementKey) ??
                activeEntries.find((candidate) => normalize(candidate.label) === normalizedTitle);
            const registeredIcon = normalizeRegisteredIcon(entry?.icon);

            if (registeredIcon) {
                return registeredIcon;
            }

            return resolveSemanticIcon(normalizedTitle, normalize(entry?.key ?? ''));
        },
        [activeEntries],
    );

    return (
        <MemoTableOfContents
            contentRef={contentRef}
            excludeHidden
            getHeadingIcon={getHeadingIcon}
            placement='sticky'
            refreshKey={pathname}
        />
    );
};

export default SettingsTableOfContents;
